<script setup>
import {
  arcPath,
  SEGMENT_CONFIG,
  ROTATION_OFFSET_DEG,
  VIEWBOX_CENTER,
  TOTAL_DEGREE_SPAN,
  SEGMENT_GAP_DEGREES,
  SEGMENTS,
  STROKE_WIDTH,
  RADIUS_RATIO,
} from "@/components/seo-score/visibility-chart/composables/radialChart";
import {computed} from "vue";

const props = defineProps({
  value: {
    type: Number,
    required: true,
  },
});

const radius = computed(() =>
  VIEWBOX_CENTER * Math.max(0, Math.min(1, RADIUS_RATIO)),
);

const segments = computed(() => {
  const usableSweep = TOTAL_DEGREE_SPAN - SEGMENT_GAP_DEGREES * (SEGMENTS - 1);
  const sweepPerSegment = usableSweep / SEGMENTS;

  const baseStart = -TOTAL_DEGREE_SPAN / 2 + ROTATION_OFFSET_DEG;
  const value = props.value;

  return SEGMENT_CONFIG.map((config, index) => {
    const startAngle = baseStart + index * (sweepPerSegment + SEGMENT_GAP_DEGREES);
    const endAngle = startAngle + sweepPerSegment;

    const rangeSpan = config.max - config.min || 1;
    const rawFraction = (value - config.min) / rangeSpan;
    const fillFraction = Math.min(1, Math.max(0, rawFraction));
    const filledEndAngle = startAngle + sweepPerSegment * fillFraction;

    return {
      index,
      startAngle,
      endAngle,
      filledEndAngle,
      fillFraction,
      strokeClass: config.strokeClass,
    };
  });
});

const filledSegments = computed(() =>
  segments.value.filter((segment) => segment.fillFraction > 0),
);
</script>

<template>
  <g
    :transform="`translate(${VIEWBOX_CENTER}, ${VIEWBOX_CENTER})`"
    filter="url(#radialBarInnerShadow)"
  >
    <!-- Background segments -->
    <path
      v-for="segment in segments"
      :key="`bg-${segment.index}`"
      :d="arcPath(segment.startAngle, segment.endAngle, radius)"
      class="tw-stroke-neutral-100"
      fill="none"
      :stroke-width="STROKE_WIDTH"
      stroke-linecap="round"
    />

    <!-- Foreground filled segments -->
    <path
      v-for="segment in filledSegments"
      :key="`fg-${segment.index}`"
      :d="arcPath(segment.startAngle, segment.filledEndAngle, radius)"
      fill="none"
      :class="[segment.strokeClass]"
      :stroke-width="STROKE_WIDTH"
      stroke-linecap="round"
    />
  </g>
</template>
